import { CCol, CFormGroup, CInput, CLabel, CRow } from "@coreui/react";
import { get } from "lodash";
import { useWatch } from "react-hook-form";
import {
  BasicNumberInput,
  DisplayRichText,
  ErrorText,
  StyledTextArea,
  TextButton,
  UploadFileToStorage,
} from "src/components";
import styled from "styled-components";
import RadioButtons from "./RadioButtons";
import { useSelector } from "react-redux";
import { selectTrack } from "src/store/projectSelectors";

const DOC_FORMATS = [
  ".csv",
  ".doc",
  ".docx",
  ".xml",
  ".pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "image/*",
];

const StyledTextButton = styled(TextButton)`
  transition: all 0.2s ease-in-out;
  &:hover {
    background-color: #0d6efd;
    color: white !important;
    border-color: #0d6efd;
    text-decoration: none !important;

    svg {
      color: white;
    }
  }
`;

const Question = ({
  area,
  idx,
  values,
  onChange,
  errors,
  q,
  handleGuidance,
  respondentId,
}) => {
  const value = useWatch({
    name: `${area}.${q.key}.score`,
  });

  const track = useSelector(selectTrack);

  const isValidationTrack = track === "validation";

  const questionText = q.label;

  const answers = q.answers;

  function convertValue(value) {
    if (value === "n/a") return value;
    if (isNaN(+value)) return value;

    return +value;
  }

  const hasGuidance = !!q.guidance?.replaceAll(/<.+?>/g, "");

  const questionType = q?.type || "select";

  function checkDateValue(dateValue) {
    if (dateValue) {
      const date = new Date(dateValue);
      if (date.toString() === "Invalid Date") {
        return;
      }
      return date.toISOString().split("T")[0];
    }
    return;
  }

  let question = null;
  if (questionType === "select") {
    question = (
      <RadioButtons
        data={answers}
        name={`${area}.${q.key}.score`}
        onChange={onChange}
        defaultValue={convertValue(get(values, `${area}.${q.key}.score`))}
        className="mt-4"
      />
    );
  } else if (questionType === "text") {
    question = (
      <StyledTextArea
        name={`${area}.${q.key}.answer`}
        className="mt-4"
        onChange={onChange}
        rows={2}
        defaultValue={get(values, `${area}.${q.key}.answer`)}
        placeholder={"Please enter your answer"}
      />
    );
  } else if (questionType === "number") {
    question = (
      <BasicNumberInput
        name={`${area}.${q.key}.answer`}
        defaultValue={get(values, `${area}.${q.key}.answer`)}
        className="mt-4"
        onChange={onChange}
        placeholder={"Please enter your answer"}
        min={q?.min}
        max={q?.max}
        suffix={q?.suffix ? " " + q.suffix : undefined}
      />
    );
  } else if (questionType === "date") {
    question = (
      <CInput
        type="date"
        name={`${area}.${q.key}.answer`}
        defaultValue={checkDateValue(get(values, `${area}.${q.key}.answer`))}
        className="mt-4"
        onChange={onChange}
      />
    );
  } else if (questionType === "date-range") {
    question = (
      <CCol className="mt-4">
        <CRow className="d-flex justify-content-between align-items-end">
          <CFormGroup className="pl-0 col">
            <CLabel>From</CLabel>

            <CInput
              type="date"
              name={`${area}.${q.key}.answer.from`}
              defaultValue={checkDateValue(
                get(values, `${area}.${q.key}.answer.from`)
              )}
              onChange={onChange}
              className="w-100"
              placeholderText={"Select date"}
            />
            <ErrorText value={errors["report-date-from"]} />
          </CFormGroup>
          <CFormGroup className="col pr-0">
            <CLabel>To</CLabel>

            <CInput
              type="date"
              onChange={onChange}
              name={`${area}.${q.key}.answer.to`}
              defaultValue={checkDateValue(
                get(values, `${area}.${q.key}.answer.to`)
              )}
              className="w-100"
              placeholderText={"Select date"}
            />
            <ErrorText value={errors["report-date-to"]} />
          </CFormGroup>
        </CRow>
      </CCol>
    );
  }

  return (
    <div id={`${area}-${q.key}`} className={`mb-5 w-100`}>
      <div className="d-flex align-items-baseline">
        <span style={{ fontSize: "20px" }} className="mr-2">
          {idx + 1 + ". "}
        </span>
        <DisplayRichText
          value={questionText}
          fontSize="20px"
          className="mr-2"
        />
        {hasGuidance && (
          <StyledTextButton
            type="button"
            onClick={handleGuidance}
            className={"text-info ml-3 d-flex p-2 border border-info "}
            style={{ fontSize: "12px", lineHeight: "1" }}
          >
            Guidance
          </StyledTextButton>
        )}
      </div>
      {question}
      <ErrorText value={get(errors, `${area}.${q.key}.score`)} />

      {isValidationTrack && !!value && (
        <>
          <StyledTextArea
            name={`${area}.${q.key}.evidence`}
            className="mt-4"
            onChange={onChange}
            rows={2}
            defaultValue={get(values, `${area}.${q.key}.evidence`)}
            placeholder={"Please justify your answer"}
          />
          <UploadFileToStorage
            storagePath={`survey/standalone/${respondentId}/${area}/${q.key}`}
            label="Upload evidence"
            acceptFormats={DOC_FORMATS.join(",")}
          />
        </>
      )}
    </div>
  );
};

export default Question;
